// src/lib/mediaHub.ts
// © SynkTribe Ltd. Centralized media navigation + helpers.
// Provides a light event-bus that binds react-router's navigate()
// without creating circular imports.

import { createSearchParams, type To } from "react-router-dom";
import { apiOrigin } from "@/lib/env";

/* ────────────────────────────────────────────────────────────────────
   Types
   ────────────────────────────────────────────────────────────────────*/
export type MediaCategory =
  | "Binaural Beats"
  | "ASMR"
  | "Breathwork"
  | "Guided Meditation"
  | "Ambient Focus";

export type MediaHubContext = {
  category?: MediaCategory;
};

export type OpenOpts = {
  source?: string; // default: "spotify"
  replace?: boolean; // default: false (push history)
  extra?: Record<string, string | number | boolean | undefined | null>;
};

type NavigateFn = (to: To, opts?: { replace?: boolean; state?: unknown }) => void;

/* ────────────────────────────────────────────────────────────────────
   Internal state
   ────────────────────────────────────────────────────────────────────*/
let navigateRef: NavigateFn | null = null;

/** Attach the router navigate so media helpers can route anywhere. */
export function bindMediaNavigator(navigate: NavigateFn) {
  navigateRef = navigate;
}

/** Detach the router navigate (e.g., on unmount/handover). */
export function unbindMediaNavigator() {
  navigateRef = null;
}

/**
 * Internal nav helper with robust fallback:
 * - Uses SPA navigation when bound.
 * - Falls back to hard redirects if not bound.
 */
function go(to: To, opts?: { replace?: boolean; state?: unknown }) {
  if (navigateRef) {
    navigateRef(to, opts);
    return;
  }
  if (typeof window !== "undefined") {
    if (typeof to === "string") {
      opts?.replace ? window.location.replace(to) : (window.location.href = to);
      return;
    }
    if (typeof to === "object" && "pathname" in to) {
      const path = `${to.pathname}${to.search ?? ""}${to.hash ?? ""}`;
      opts?.replace ? window.location.replace(path) : (window.location.href = path);
    }
  }
}

/* ────────────────────────────────────────────────────────────────────
   Public helpers (stable across SynkTribe)
   ────────────────────────────────────────────────────────────────────*/

/**
 * Open the Media Hub page.
 *
 * New style:
 *   openMediaDashboard("Ambient Focus", { source: "spotify", extra: { intent: "preview" }})
 *
 * Back-compat (old ctx style):
 *   openMediaDashboard({ category: "Ambient Focus" })
 *
 * If no category is provided, defaults to "Ambient Focus".
 */
export function openMediaDashboard(
  categoryOrCtx?: MediaCategory | MediaHubContext,
  opts: OpenOpts = {}
) {
  const category: MediaCategory =
    typeof categoryOrCtx === "string"
      ? categoryOrCtx
      : categoryOrCtx?.category ?? "Ambient Focus";

  const search = buildMediaSearchParams(category, opts);

  // Fire an event for analytics/side-effects (doesn't render anything)
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent("open-media-dashboard", {
          detail: {
            category,
            ...sanitizeExtras({ source: opts.source ?? "spotify", ...opts.extra }),
          },
        })
      );
    } catch {
      /* no-op */
    }
  }

  // Route to the hub page (SPA if bound, hard redirect otherwise)
  go({ pathname: "/media", search: `?${search}` }, { replace: !!opts.replace });
}

/** Legacy convenience. */
export function openMediaRoot() {
  go("/media");
}

/** Open a specific media item by id. */
export function openMediaItem(id: string) {
  go(`/media/${encodeURIComponent(id)}`);
}

/** Deep-link into a capture/recorder flow. */
export function openRecorder(kind: "audio" | "video" | "screen" = "audio") {
  go(`/media/record/${kind}`);
}

/** Robust Spotify opener with proxy/absolute origin support.
 *
 * - If VITE_USE_PROXY=true → hits local proxy:        /api/spotify/login
 * - Else → hits absolute backend from VITE_API_ORIGIN (apiOrigin()).
 * - Always includes a return URL for post-OAuth redirect.
 */
export function openSpotifyConnect() {
  if (typeof window === "undefined") return;
  const base = apiOrigin(); // "" if proxy, or e.g. "https://api.synktribe.com" in prod
  const path = "/api/spotify/login";
  const redirect = encodeURIComponent(`${window.location.origin}/spotify/callback`);
  const url = `${base}${path}?redirect=${redirect}`;
  // Hard redirect to ensure OAuth redirect works consistently
  window.location.assign(url);
}

/**
 * Binaural dashboard (primary route).
 * Keep this simple route per current IA; route aliases can co-exist below.
 */
export function openBinauralDash() {
  go("/binaural");
}

/** Full Neural Readiness dashboard (dedicated page). */
export function openNeuralReadinessDash() {
  go("/readiness");
}

/** Optional legacy alias: some builds still use /dash/binaural. */
export function openBinauralDashLegacy(opts: OpenOpts = {}) {
  const search = createSearchParams({
    ...(opts.source ? { source: String(opts.source) } : {}),
    ...sanitizeExtras(opts.extra),
  }).toString();

  const to = { pathname: "/dash/binaural", search: search ? `?${search}` : "" };
  go(to, { replace: !!opts.replace });
}

/** Return true if navigation is currently bound (for diagnostics). */
export function isMediaNavigatorBound() {
  return !!navigateRef;
}

/* ────────────────────────────────────────────────────────────────────
   Utilities
   ────────────────────────────────────────────────────────────────────*/

/** Build search params for the Media Hub route. */
function buildMediaSearchParams(category: MediaCategory, opts: OpenOpts) {
  const params = createSearchParams({
    category,
    source: opts.source ?? "spotify",
    ...sanitizeExtras(opts.extra),
  });
  return params.toString();
}

/** Convert extras into flat string pairs (drops null/undefined/objects). */
function sanitizeExtras(
  extra?: Record<string, string | number | boolean | undefined | null>
): Record<string, string> {
  if (!extra) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(extra)) {
    if (v === undefined || v === null) continue;
    const t = typeof v;
    if (t === "string" || t === "number" || t === "boolean") {
      out[k] = String(v);
    }
  }
  return out;
}
